// The IB History Paper 1 practice player (profile 'source-paper'): the start
// screen with the choice of variant source, the guided rail, Revise (the menu
// and the reveal-tile carousel for both packs), how Paper 1 works, and the
// source booklet. Leah's engine spec §6, faithful to her reference build.
//
// This build (step 2 of the plan) plays everything up to the questions. Each
// question step shows the question, what it asks and the booklet with its
// sources marked; the pick, sort and writing screens arrive in step 3, the
// source table and the send panel in step 4, the timed paper in step 5.
//
// State lives in useSourcePaper; this file is layout. Every word that names
// the practice's history comes from the block; the engine's own lines are in
// lib/revise/sourcePaper/steps.ts.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReviseImage, ReviseRun, SourcePaperBlock, SourcePaperQuestion } from '../../../../../../lib/revise/types';
import { SOURCE_PAPER_FORMAT } from '../../../../../../lib/revise/types';
import {
  bodyFor,
  packCards,
  packHeading,
  paperLines,
  questionText,
  reviseLines,
  revisionTiles,
  roleAsks,
  roleMinutes,
  variantHelp,
  type RevisePack,
} from '../../../../../../lib/revise/sourcePaper/steps';
import { fetchReviseProgress, saveReviseDraft } from '../../../../../../services/reviseService';
import { reportError } from '../../../../../../lib/errorHandling';
import type { ReviseRunSlideData } from '../../../slideTypes';
import { REVISE_STYLES } from '../styles';
import { WORKSHOP_STYLES } from '../workshop/styles';
import { SOURCE_PAPER_STYLES } from './styles';
import { useSourcePaper, type SourcePaperApi } from './useSourcePaper';

const STYLES = REVISE_STYLES + WORKSHOP_STYLES + SOURCE_PAPER_STYLES;

/** A question step's heading until its own sub-steps arrive (step 3). */
const ROLE_TITLES: Record<SourcePaperQuestion['role'], string> = {
  extract: 'What the source says',
  visual: 'What the source suggests',
  opvl: 'Value and limitations',
  compare: 'Compare and contrast',
  essay: 'Sources and own knowledge',
};

interface Props {
  run: ReviseRun;
  data: ReviseRunSlideData;
  p: SourcePaperBlock;
  /** The run's CSS variables, from reviseTokens. */
  tokens: React.CSSProperties;
  slideId?: string;
  courseId?: string;
  lessonId?: string;
}

/** The pending save per (slide, key), so saves for one row never overtake each other. */
const saveChains = new Map<string, Promise<void>>();

/** Student entry: loads resume state, then plays. */
export function SourcePaperRunStudent({ run, data, p, tokens, slideId, courseId, lessonId }: Props) {
  const [progress, setProgress] = useState<Record<string, Record<string, unknown>> | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    if (!slideId || !courseId) {
      setProgress(null);
      return;
    }
    fetchReviseProgress(courseId, slideId).then((r) => live && setProgress(r));
    return () => {
      live = false;
    };
  }, [slideId, courseId]);

  // Every row is an upsert, and needs the lesson id; a preview keeps state in
  // memory. A skip for the flag or for staff previewing is expected; anything
  // else is lost progress and must be seen.
  const onSave = useMemo(
    () =>
      slideId && courseId && lessonId
        ? (key: string, payload: Record<string, unknown>) => {
            const chainKey = `${slideId}:${key}`;
            const prior = saveChains.get(chainKey) ?? Promise.resolve();
            const next = prior
              .then(() => saveReviseDraft(courseId, lessonId, { id: slideId, data, order: 0 }, key, payload))
              .then((r) => {
                if (!r.written && r.skipReason !== 'flag-off' && r.skipReason !== 'not-enrolled') reportError('reviseSourcePaper.save', new Error(r.skipReason ?? 'not written'), { key });
              })
              .finally(() => {
                if (saveChains.get(chainKey) === next) saveChains.delete(chainKey);
              });
            saveChains.set(chainKey, next);
          }
        : undefined,
    [slideId, courseId, lessonId, data],
  );

  return (
    <div className="rr" style={tokens} data-profile={run.profile} data-board={run.board}>
      <style>{STYLES}</style>
      {progress === undefined && slideId && courseId ? <div className="rr-stub">Loading…</div> : <SourcePaperPlayer p={p} images={run.images ?? []} initialProgress={progress ?? null} onSave={onSave} />}
    </div>
  );
}

export function SourcePaperPlayer({
  p,
  images,
  initialProgress,
  onSave,
}: {
  p: SourcePaperBlock;
  images: ReviseImage[];
  initialProgress: Record<string, Record<string, unknown>> | null;
  onSave?: (key: string, payload: Record<string, unknown>) => void;
}) {
  const api = useSourcePaper({ p, initialProgress, onSave });
  const body = useRef<HTMLDivElement>(null);
  const topic = api.revPart ? api.packs[api.revPart].topic : -1;
  // Each move starts at the top of its screen, as the reference build does.
  useEffect(() => {
    body.current?.scrollTo?.({ top: 0 });
  }, [api.phase, api.stepIdx, api.revPart, topic]);

  return (
    <div className="rw rp">
      <div className="rw-band">
        <span className="rr-eyebrow">IB History · Paper 1 · {p.brandline}</span>
        <h1>
          {p.title} <span>{p.titleAccent}</span>
        </h1>
        {api.phase === 'guided' && (
          <button type="button" className="rw-essaypill" onClick={() => api.goToId('answers')}>
            Your answers: 0 words
          </button>
        )}
      </div>
      <div className="rw-body" ref={body}>
        {api.phase === 'start' ? <StartScreen api={api} /> : <Guided api={api} images={images} />}
      </div>
      {api.phase === 'guided' && <Nav api={api} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Start screen (spec §6.2)
// ---------------------------------------------------------------------------

function StartScreen({ api }: { api: SourcePaperApi }) {
  const { p, variants } = api;
  const help = variantHelp(p);
  return (
    <div className="rw-start rp-start">
      <p className="rw-lede">{p.intro}</p>
      <div className="rw-row">
        <button type="button" className="rr-btn-primary" onClick={api.begin}>
          {api.resuming ? 'Carry on where you left off' : 'Practise question by question'}
        </button>
      </div>
      <div className="rw-startq rp-startq">
        <span className="rr-eyebrow">The questions you will answer</span>
        <ol>
          {p.questions.map((q) => (
            <li key={q.id}>
              <b>{q.n}</b>
              {questionText(q, api.variant)}
              <span className="rp-mk">[{q.marks}]</span>
            </li>
          ))}
        </ol>
        <p className="rw-exam">Paper 1: one hour, four questions, {SOURCE_PAPER_FORMAT.totalMarks} marks.</p>
      </div>
      {variants && (
        <div className="rw-card">
          <span className="rr-eyebrow">Choose your Source {variants.letter}</span>
          {help && <p className="rw-howto">{help}</p>}
          <div className="rp-ver" role="group" aria-label={`Choose your Source ${variants.letter}`}>
            {Object.entries(variants.variants).map(([id, v]) => (
              <button key={id} type="button" className="rw-opt" aria-pressed={api.variant === id} onClick={() => api.setVariant(id)}>
                <b>
                  Source {variants.letter}: {v.label}
                </b>
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <span className="rr-eyebrow">How it works</span>
        <div className="rw-how" style={{ marginTop: 8 }}>
          {p.how.map((h, i) => (
            <div key={i} className="rw-card">
              <div className="rw-num">{i + 1}</div>
              <h3>{h.head}</h3>
              <p>{h.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Guided: rail and the step body
// ---------------------------------------------------------------------------

function Guided({ api, images }: { api: SourcePaperApi; images: ReviseImage[] }) {
  const s = api.step;
  return (
    <div className="rw-wrap">
      <nav className="rw-rail" aria-label="Paper 1 steps">
        {api.steps.map((st, i) => {
          const done = api.isDone(st);
          return (
            <button key={st.id} type="button" className={`rw-railbtn${done ? ' rw-done' : ''}`} aria-current={i === api.stepIdx ? 'step' : undefined} onClick={() => api.goTo(i)}>
              <span className="rw-n">{done ? '✓' : i + 1}</span>
              <span className="rw-lbl">{st.title}</span>
            </button>
          );
        })}
        <button type="button" className="rw-railhome" onClick={api.toStart}>
          Back to the start
        </button>
      </nav>
      <div className="rw-main" key={`${s?.id}-${api.revPart ?? ''}`}>
        {s?.id === 'revise' && <ReviseStep api={api} />}
        {s?.id === 'paper' && <PaperStep api={api} images={images} />}
        {s?.question && <QuestionStep api={api} q={s.question} images={images} />}
        {s?.id === 'answers' && <AnswersStep api={api} />}
      </div>
    </div>
  );
}

function Head({ label, title, children }: { label: string; title: string; children?: React.ReactNode }) {
  return (
    <div className="rw-head">
      <div>
        <span className="rr-eyebrow">{label}</span>
        <h2>{title}</h2>
      </div>
      {children}
    </div>
  );
}

/** The bottom bar: Back on the left, the step's next move on the right. */
function Nav({ api }: { api: SourcePaperApi }) {
  const move = navFor(api);
  return (
    <div className="rr-nav">
      <button type="button" onClick={move.back ?? undefined} disabled={!move.back}>
        ← Back
      </button>
      <div className="rr-nav-right" style={{ alignItems: 'center' }}>
        <button type="button" className="rr-btn-primary" onClick={move.next}>
          {move.label}
        </button>
      </div>
    </div>
  );
}

function navFor(api: SourcePaperApi): { back: (() => void) | null; next: () => void; label: string } {
  const { stepIdx, steps, step } = api;
  const after = steps[stepIdx + 1];
  const toPrev = stepIdx > 0 ? () => api.goTo(stepIdx - 1) : null;
  if (step?.id === 'revise') {
    const pack = api.revPart;
    if (!pack) return { back: null, next: () => api.goToId('paper'), label: 'Skip to the paper' };
    const cards = packCards(api.p, pack);
    const t = api.packs[pack].topic;
    const last = t >= cards.length - 1;
    return {
      back: t > 0 ? () => api.toTopic(pack, t - 1) : api.closePack,
      next: last ? () => api.finishPack(pack) : () => api.toTopic(pack, t + 1),
      label: last ? 'Finish this revision' : `Next: ${cards[t + 1].title}`,
    };
  }
  if (step?.id === 'answers') return { back: toPrev, next: api.toStart, label: 'Back to the start' };
  return { back: toPrev, next: () => api.goTo(stepIdx + 1), label: after ? `Next: ${after.title}` : 'Next' };
}

// ---------------------------------------------------------------------------
// Revise (spec §6.3)
// ---------------------------------------------------------------------------

function ReviseStep({ api }: { api: SourcePaperApi }) {
  const pack = api.revPart;
  if (!pack) return <ReviseMenu api={api} />;
  return <ReviseTopic api={api} pack={pack} />;
}

function ReviseMenu({ api }: { api: SourcePaperApi }) {
  const lines = reviseLines(api.p);
  return (
    <>
      <Head label="Optional" title="Revise before you start" />
      <p className="rw-lede">{lines.lede}</p>
      <div className="rp-two">
        {(['content', 'skills'] as RevisePack[]).map((pack) => {
          const l = lines[pack];
          const done = api.packs[pack].done;
          return (
            <div key={pack} className="rw-card">
              <span className="rr-eyebrow">{done ? 'Done' : l.label}</span>
              <h4>{l.title}</h4>
              <p>{l.text}</p>
              <button type="button" className={done ? 'rr-btn-quiet' : 'rr-btn-primary'} onClick={() => api.openPack(pack)}>
                {done ? 'Revise again' : 'Start'}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ReviseTopic({ api, pack }: { api: SourcePaperApi; pack: RevisePack }) {
  const cards = packCards(api.p, pack);
  const t = Math.min(api.packs[pack].topic, cards.length - 1);
  const card = cards[t];
  const tiles = revisionTiles(card);
  const open = new Set(api.packs[pack].open[t] ?? []);
  const shown = tiles.filter((x) => open.has(x.id)).length;
  return (
    <>
      <Head label={packHeading(api.p, pack)} title={card.title}>
        <div className="rw-dots" aria-label="Topics">
          {cards.map((c, i) => (
            <button key={i} type="button" aria-label={`Card ${i + 1}: ${c.title}`} title={c.title} aria-current={i === t ? 'true' : undefined} onClick={() => api.toTopic(pack, i)} />
          ))}
          <span className="rw-meta">
            Card {t + 1} of {cards.length}
          </span>
        </div>
      </Head>
      <div className="rp-revbar">
        <p className="rw-howto" aria-live="polite">
          Click each card to reveal it. {shown} of {tiles.length} revealed.
        </p>
        <button type="button" className="rp-link" onClick={() => api.toggleAll(pack, t)}>
          {shown < tiles.length ? 'Reveal all' : 'Hide all'}
        </button>
      </div>
      <div className="rp-revgrid">
        {tiles.map((tile, i) => {
          const on = open.has(tile.id);
          return (
            <button key={tile.id} type="button" className={`rp-tile${on ? ' rp-open' : ''}`} aria-expanded={on} onClick={() => api.toggleTile(pack, t, tile.id)}>
              <span className="rp-tnum">{i + 1}</span>
              <span className="rp-front">{tile.front}</span>
              {!on && <span className="rp-hint">Click to reveal</span>}
              {on && tile.back !== undefined && <span className="rp-back">{tile.back}</span>}
              {on && tile.values !== undefined && (
                <span className="rp-cols">
                  <span className="rp-col">
                    <span className="rp-clab">Possible values</span>
                    {tile.values}
                  </span>
                  <span className="rp-col rp-l">
                    <span className="rp-clab">Possible limitations</span>
                    {tile.limitations}
                  </span>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// How Paper 1 works (spec §6.4)
// ---------------------------------------------------------------------------

function PaperStep({ api, images }: { api: SourcePaperApi; images: ReviseImage[] }) {
  const { p } = api;
  const lines = paperLines(p);
  return (
    <>
      <Head label="Before you start" title="How Paper 1 works" />
      <p className="rw-lede">{lines.lede}</p>
      <p className="rw-howto">{p.caseStudy}</p>
      <div className="rp-rows">
        {p.questions.map((q) => (
          <div key={q.id} className="rp-row">
            <span className="rr-eyebrow">
              Question {q.n} [{q.marks} marks] · about {roleMinutes(q)} minutes
            </span>
            <h4>{questionText(q, api.variant)}</h4>
            <p>{roleAsks(q)}</p>
          </div>
        ))}
      </div>
      <div className="rp-note">
        <span className="rr-eyebrow">Read the sources first</span>
        <p>{lines.readFirst}</p>
      </div>
      <h3 style={{ fontSize: '1.35rem' }}>{lines.buildHead}</h3>
      <p className="rw-howto">{lines.buildText}</p>
      <div className="rw-how">
        {lines.moves.map((m, i) => (
          <div key={i} className="rw-card">
            <div className="rw-num">{i + 1}</div>
            <h3>{m.head}</h3>
            <p>{m.text}</p>
          </div>
        ))}
      </div>
      <p className="rw-howto">{lines.saved}</p>
      <Booklet api={api} screen="paper" images={images} />
    </>
  );
}

// ---------------------------------------------------------------------------
// A question step. The question's own screens arrive in step 3; until then it
// shows the question, what it asks and the booklet with its sources marked.
// ---------------------------------------------------------------------------

function QuestionStep({ api, q, images }: { api: SourcePaperApi; q: SourcePaperQuestion; images: ReviseImage[] }) {
  return (
    <>
      <Head label={`Question ${q.n}`} title={ROLE_TITLES[q.role]} />
      <div className="rw-qstrip">
        <span className="rr-eyebrow">
          Question {q.n} [{q.marks} marks]
        </span>
        <span>{questionText(q, api.variant)}</span>
      </div>
      <div className="rp-split">
        <Booklet api={api} screen={q.id} only={q.sources} images={images} />
        <div className="rp-task">
          <div className="rw-card">
            <span className="rr-eyebrow">What this question asks</span>
            <p>{roleAsks(q)}</p>
            <p className="rw-howto">About {roleMinutes(q)} minutes in the exam.</p>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Your answers (spec §6.10). The answers and the send panel fill in with the
// question screens; until then every question reads as not yet answered.
// ---------------------------------------------------------------------------

function AnswersStep({ api }: { api: SourcePaperApi }) {
  return (
    <>
      <Head label="Finish" title="Your answers" />
      <p className="rw-lede">Read your answers through once more. Check each one against the question and the marks it carries.</p>
      {api.p.questions.map((q) => (
        <div key={q.id} className="rw-card">
          <span className="rr-eyebrow">
            Question {q.n} [{q.marks} marks] · 0 words
          </span>
          <p style={{ fontWeight: 600 }}>{questionText(q, api.variant)}</p>
          <div className="rp-answer rp-empty">You have not answered this question yet.</div>
          <div className="rw-row">
            <button type="button" className="rr-btn-quiet" onClick={() => api.goToId(q.role)}>
              Edit this answer
            </button>
          </div>
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// The source booklet (spec §6.5)
// ---------------------------------------------------------------------------

function Booklet({ api, screen, only, images }: { api: SourcePaperApi; screen: string; only?: string[]; images: ReviseImage[] }) {
  const letters = Object.keys(api.p.sources);
  const cur = api.tabs[screen] ?? only?.[0] ?? letters[0];
  return (
    <section className="rp-booklet" aria-label="Source booklet">
      <div className="rp-bhead">
        <span className="rr-eyebrow">Source booklet</span>
        <div className="rp-btabs" role="tablist" aria-label="Sources">
          {letters.map((l) => (
            <button key={l} type="button" role="tab" aria-selected={l === cur} className={`rp-btab${only?.includes(l) ? ' rp-rel' : ''}`} onClick={() => api.setTab(screen, l)}>
              Source {l}
            </button>
          ))}
        </div>
      </div>
      <div className="rp-bbody" role="tabpanel" aria-label={`Source ${cur}`}>
        <SourceView api={api} letter={cur} images={images} />
      </div>
    </section>
  );
}

function SourceView({ api, letter, images }: { api: SourcePaperApi; letter: string; images: ReviseImage[] }) {
  const body = bodyFor(api.p, letter, api.variant);
  if (!body) return null;
  if (body.type === 'image') {
    const img = images.find((i) => i.id === body.imageId);
    return (
      <div className="rp-src">
        <h3>Source {letter}</h3>
        <p className="rp-who">{body.who}</p>
        {img?.url ? (
          <img className="rp-cartoon" src={img.url} alt={img.alt} />
        ) : (
          <div className="rp-cartoon rp-ph" role="img" aria-label={img?.alt ?? `Source ${letter}`}>
            <span className="rr-eyebrow">Image</span>
            <p>{img?.alt}</p>
          </div>
        )}
        {body.caption && <p className="rp-cap">{body.caption}</p>}
      </div>
    );
  }
  return (
    <div className="rp-src">
      <h3>Source {letter}</h3>
      <p className="rp-who">{body.who}</p>
      {(body.paragraphs ?? []).map((t, i) => (
        <p key={i} className="rp-stext">
          {t}
        </p>
      ))}
      {body.gloss && <p className="rp-gloss">{body.gloss}</p>}
    </div>
  );
}
